import Phaser from 'phaser';

export type TrayRect = { x: number; y: number; w: number; h: number };

export type SnapResult = { row: number; col: number; snapX: number; snapY: number } | null;

export type CustomDragControllerCallbacks = {
  canStartDrag: (pieceId: number) => boolean;
  /**
   * Вызывается при дропе на доску (inside + snap != null).
   * Контроллер уже скрыл оригинал в лотке и создаёт клон, который будет уничтожен.
   */
  onDropToBoard: (args: {
    pieceId: number;
    pieceKey: string;
    tileW: number;
    tileH: number;
    snap: { row: number; col: number; snapX: number; snapY: number };
    originalImg: Phaser.GameObjects.Image;
  }) => void;
  /**
   * Вызывается если дроп невалиден (вне доски или snap=null) — нужно вернуть деталь в лоток.
   */
  onReturnToTray: (args: { pieceId: number; originalImg: Phaser.GameObjects.Image }) => void;
};

export class CustomDragController {
  private scene: Phaser.Scene;
  private zone: Phaser.GameObjects.Zone | null = null;

  private trayPanel: any | null = null;
  private trayImages: Phaser.GameObjects.Image[] = [];
  private getTrayImages: (() => Phaser.GameObjects.Image[]) | null = null;

  private sliderH = 0;
  private callbacks: CustomDragControllerCallbacks | null = null;

  private clone: Phaser.GameObjects.Image | null = null;
  private originalImg: Phaser.GameObjects.Image | null = null;
  private pieceId: number | null = null;

  private onMove?: (p: Phaser.Input.Pointer) => void;
  private onUp?: (p: Phaser.Input.Pointer) => void;

  // Gesture arbitration state
  private gestureActive = false;
  private gestureMode: 'NONE' | 'SCROLL' | 'DRAG' = 'NONE';
  private startX = 0;
  private startY = 0;
  private lastX = 0;
  private trayCanScroll = false;
  private candidateImg: Phaser.GameObjects.Image | null = null;
  private gestureDragStartPx = 6;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  attach(args: {
    tr: TrayRect;
    sliderH: number;
    trayPanel: any | null;
    /** Исходный список картинок лотка (для обратной совместимости). */
    trayImages: Phaser.GameObjects.Image[];
    /** Если задан — при pointerdown берём актуальный список из лотка (включая куски, возвращённые с доски). */
    getTrayImages?: () => Phaser.GameObjects.Image[];
    callbacks: CustomDragControllerCallbacks;
    snapWorld: (x: number, y: number) => SnapResult;
    getBoardRect: () => { x: number; y: number; w: number; h: number } | null;
    getBoardPieceSize: () => { w: number; h: number } | null;
    /** Вызывается при входе в режим drag из лотка. */
    onDragStart?: () => void;
    /** Вызывается при входе в режим drag с pieceId перетаскиваемого кусочка. */
    onDragStartWithPiece?: (pieceId: number) => void;
    /** Вызывается при завершении drag (дроп или возврат в лоток). */
    onDragEnd?: () => void;
    /** Если задан и возвращает false — скролл лотка отключён (колёсико и сдвиг по горизонтали). */
    isScrollEnabled?: () => boolean;
  }): void {
    this.callbacks = args.callbacks;
    this.trayPanel = args.trayPanel;
    this.trayImages = args.trayImages;
    this.getTrayImages = args.getTrayImages ?? null;
    this.sliderH = args.sliderH;

    // (re)create overlay zone
    const contentZoneH = args.tr.h - this.sliderH;
    if (this.zone) this.zone.destroy();
    this.zone = this.scene.add
      .zone(args.tr.x, args.tr.y, args.tr.w, contentZoneH)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(3000)
      .setInteractive({ useHandCursor: false });

    this.zone.on('wheel', (_pointer: Phaser.Input.Pointer, dx: number, dy: number, _dz: number, ev?: WheelEvent) => {
      const scrollOk = args.isScrollEnabled === undefined || args.isScrollEnabled();
      if (!scrollOk) return;
      const panel = this.trayPanel as any;
      if (!panel) return;
      const delta = (ev && ev.deltaX !== 0) ? ev.deltaX : (dx !== 0 ? dx * 50 : dy * 50);
      if (typeof panel.addChildOX === 'function') {
        panel.addChildOX(delta, true);
      }
    });

    const SCROLL_START_PX = 5;
    const DRAG_START_PX_MOUSE = 6;
    const DRAG_START_PX_TOUCH = 4;
    const HIT_EXPAND_PX_MOUSE = 10;
    const HIT_EXPAND_PX_TOUCH = 18;
    const HORIZONTAL_DOMINANCE = 1.2;

    const isTouchPointer = (pointer: Phaser.Input.Pointer): boolean => {
      const ev = pointer.event as unknown as { pointerType?: string } | undefined;
      return ev?.pointerType === 'touch' || (pointer as unknown as { wasTouch?: boolean }).wasTouch === true;
    };

    const pickPieceUnderPointer = (wx: number, wy: number, hitExpandPx: number): Phaser.GameObjects.Image | null => {
      const images = this.getTrayImages ? this.getTrayImages() : this.trayImages;
      for (const img of images) {
        if (!img || !img.active || !img.visible) continue;
        const pid = img.getData?.('pieceId') as number | undefined;
        if (pid == null) continue;
        if (!this.callbacks?.canStartDrag(pid)) continue;
        const b = img.getBounds();
        const ex = b.x - hitExpandPx;
        const ey = b.y - hitExpandPx;
        const ew = b.width + hitExpandPx * 2;
        const eh = b.height + hitExpandPx * 2;
        if (wx >= ex && wx <= ex + ew && wy >= ey && wy <= ey + eh) return img;
      }
      return null;
    };

    // rexUI при scrollMode=1 (горизонтальный) в ScrollableBlock меняет оси:
    // диапазон скролла в topChildOY/bottomChildOY, значение — в childOY.
    const getScrollBounds = (): { min: number; max: number } | null => {
      const panel = this.trayPanel as any;
      if (!panel) return null;
      const block = panel.childrenMap?.scrollableBlock ?? panel.childrenMap?.child ?? panel;
      const scrollMode = block?.scrollMode ?? panel?.scrollMode ?? 0;
      if (scrollMode === 1) {
        const top = block?.topChildOY;
        const bottom = block?.bottomChildOY;
        if (typeof top !== 'number' || typeof bottom !== 'number') return null;
        return { min: bottom, max: top };
      }
      const left = block?.leftChildOX;
      const right = block?.rightChildOX;
      if (typeof left !== 'number' || typeof right !== 'number') return null;
      return { min: right, max: left };
    };

    const setPanelScrollX = (x: number): void => {
      const panel = this.trayPanel as any;
      if (!panel) return;
      const bounds = getScrollBounds();
      let nx = x;
      if (bounds) {
        const lo = Math.min(bounds.min, bounds.max);
        const hi = Math.max(bounds.min, bounds.max);
        nx = Phaser.Math.Clamp(x, lo, hi);
      }
      const block = panel.childrenMap?.scrollableBlock ?? panel.childrenMap?.child ?? panel;
      const scrollMode = block?.scrollMode ?? panel?.scrollMode ?? 0;
      if (scrollMode === 1) {
        if (typeof panel.setChildOY === 'function') panel.setChildOY(nx);
        else (panel as any).childOY = nx;
      } else {
        if (typeof panel.setChildOX === 'function') panel.setChildOX(nx);
        else (panel as any).childOX = nx;
      }
    };

    const getPanelScrollX = (): number => {
      const panel = this.trayPanel as any;
      if (!panel) return 0;
      const block = panel.childrenMap?.scrollableBlock ?? panel.childrenMap?.child ?? panel;
      const scrollMode = block?.scrollMode ?? panel?.scrollMode ?? 0;
      const v = scrollMode === 1 ? panel.childOY : panel.childOX;
      return typeof v === 'number' ? (v as number) : 0;
    };

    this.zone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.callbacks) return;

      this.gestureActive = true;
      this.gestureMode = 'NONE';
      this.startX = pointer.worldX;
      this.startY = pointer.worldY;
      this.lastX = this.startX;
      this.candidateImg = null;
      const isTouch = isTouchPointer(pointer);
      this.gestureDragStartPx = isTouch ? DRAG_START_PX_TOUCH : DRAG_START_PX_MOUSE;

      const bounds = getScrollBounds();
      const scrollAllowed = args.isScrollEnabled === undefined || args.isScrollEnabled();
      this.trayCanScroll = scrollAllowed && !!(bounds && Math.abs(bounds.max - bounds.min) > 1e-3);

      const hit = pickPieceUnderPointer(
        this.startX,
        this.startY,
        isTouch ? HIT_EXPAND_PX_TOUCH : HIT_EXPAND_PX_MOUSE
      );
      this.candidateImg = hit;
    });

    this.zone.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.gestureActive) return;

      const dx = pointer.worldX - this.startX;
      const dy = pointer.worldY - this.startY;
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      const horizontalLike =
        absDx > SCROLL_START_PX && absDx > absDy * HORIZONTAL_DOMINANCE;

      if (this.gestureMode === 'NONE') {
        if (this.trayCanScroll && horizontalLike) {
          this.gestureMode = 'SCROLL';
          this.lastX = pointer.worldX;
          return;
        }

        const dist = Math.sqrt(dx * dx + dy * dy);
        if (this.candidateImg && dist > this.gestureDragStartPx && !horizontalLike) {
          const hit = this.candidateImg;
          const pieceId = hit.getData('pieceId') as number;
          const pieceKey = hit.getData('pieceKey') as string;
          const tileW = hit.getData('tileW') as number;
          const tileH = hit.getData('tileH') as number;

          hit.setVisible(false);
          const world = hit.getWorldTransformMatrix();
          const tx = world.tx;
          const ty = world.ty;

          const clone = this.scene.add
            .image(tx, ty, pieceKey)
            .setOrigin(0.5, 0.5)
            .setDisplaySize(tileW, tileH);
          clone.setDepth(100000);
          clone.setData('pieceId', pieceId);
          clone.setData('pieceKey', pieceKey);
          clone.setData('tileW', tileW);
          clone.setData('tileH', tileH);

          this.clone = clone;
          this.originalImg = hit;
          this.pieceId = pieceId;

          this.gestureMode = 'DRAG';
          args.onDragStart?.();
          args.onDragStartWithPiece?.(pieceId);

          // Подписываемся на сцену, чтобы клон следовал за пальцем по всему экрану
          // и отпускание обрабатывалось даже вне зоны лотка.
          this.onMove = (p: Phaser.Input.Pointer) => {
            const dragObj = this.clone;
            if (dragObj && dragObj.scene) {
              dragObj.setPosition(p.worldX, p.worldY);

              const trayW = dragObj.getData('tileW') as number;
              const trayH = dragObj.getData('tileH') as number;
              const boardPieceSize = args.getBoardPieceSize();
              const boardRect = args.getBoardRect();
              const isOverBoard =
                !!boardRect &&
                p.worldX >= boardRect.x &&
                p.worldX <= boardRect.x + boardRect.w &&
                p.worldY >= boardRect.y &&
                p.worldY <= boardRect.y + boardRect.h;

              if (isOverBoard && boardPieceSize) {
                if (
                  Math.abs(dragObj.displayWidth - boardPieceSize.w) > 0.1 ||
                  Math.abs(dragObj.displayHeight - boardPieceSize.h) > 0.1
                ) {
                  dragObj.setDisplaySize(boardPieceSize.w, boardPieceSize.h);
                }
              } else if (
                typeof trayW === 'number' &&
                typeof trayH === 'number' &&
                (Math.abs(dragObj.displayWidth - trayW) > 0.1 ||
                  Math.abs(dragObj.displayHeight - trayH) > 0.1)
              ) {
                dragObj.setDisplaySize(trayW, trayH);
              }
            }
          };
          this.onUp = (p: Phaser.Input.Pointer) => {
            finishDrag(p);
            resetGesture();
          };
          this.scene.input.on('pointermove', this.onMove);
          this.scene.input.once('pointerup', this.onUp);
          this.scene.input.once('pointerupoutside', this.onUp);
          return;
        }
        return;
      }

      if (this.gestureMode === 'SCROLL') {
        const deltaX = pointer.worldX - this.lastX;
        this.lastX = pointer.worldX;
        if (!this.trayCanScroll || deltaX === 0) return;
        const panel = this.trayPanel as any;
        const block = panel?.childrenMap?.scrollableBlock ?? panel?.childrenMap?.child ?? panel;
        const scrollMode = block?.scrollMode ?? panel?.scrollMode ?? 0;
        if (scrollMode === 1 && typeof panel.addChildOY === 'function') {
          panel.addChildOY(deltaX, true);
        } else if (panel && typeof panel.addChildOX === 'function') {
          panel.addChildOX(deltaX, true);
        } else {
          setPanelScrollX(getPanelScrollX() + deltaX);
        }
        return;
      }

      // В режиме DRAG движение и отпускание обрабатываются сценовыми слушателями выше.
    });

    const finishDrag = (pointer: Phaser.Input.Pointer) => {
      const cb = this.callbacks;
      const dragObj = this.clone;
      const original = this.originalImg;
      const pid = this.pieceId;
      args.onDragEnd?.();
      if (!cb || !dragObj || !original || pid == null) return;

      const wx2 = pointer.worldX;
      const wy2 = pointer.worldY;
      const r = args.getBoardRect();
      const inside =
        r &&
        wx2 >= r.x &&
        wx2 <= r.x + r.w &&
        wy2 >= r.y &&
        wy2 <= r.y + r.h;

      if (inside && r) {
        const snap = args.snapWorld(wx2, wy2);
        if (snap) {
          cb.onDropToBoard({
            pieceId: pid,
            pieceKey: dragObj.getData('pieceKey') as string,
            tileW: dragObj.getData('tileW') as number,
            tileH: dragObj.getData('tileH') as number,
            snap,
            originalImg: original,
          });
          this.cleanupAfterDrop();
          return;
        }
      }

      cb.onReturnToTray({ pieceId: pid, originalImg: original });
      this.cleanupAfterDrop();
    };

    const resetGesture = () => {
      if (this.onMove) {
        this.scene.input.off('pointermove', this.onMove);
        this.onMove = undefined;
      }
      if (this.onUp) {
        this.scene.input.off('pointerup', this.onUp);
        this.scene.input.off('pointerupoutside', this.onUp);
        this.onUp = undefined;
      }
      this.gestureActive = false;
      this.gestureMode = 'NONE';
      this.candidateImg = null;
    };

    this.zone.on('pointerup', (_pointer: Phaser.Input.Pointer) => {
      if (!this.gestureActive) return;
      if (this.gestureMode === 'DRAG') return; // отпускание обработает сценовый pointerup/pointerupoutside
      resetGesture();
    });

    this.zone.on('pointerupoutside', (_pointer: Phaser.Input.Pointer) => {
      if (!this.gestureActive) return;
      if (this.gestureMode === 'DRAG') return; // отпускание обработает сценовый pointerup/pointerupoutside
      resetGesture();
    });
  }

  /** Обновить позицию/размер overlay-зоны при resize. */
  updateRect(tr: TrayRect, sliderH: number): void {
    this.sliderH = sliderH;
    if (!this.zone) return;
    this.zone.setPosition(tr.x, tr.y);
    this.zone.setSize(tr.w, tr.h - sliderH);
  }

  detach(): void {
    this.cleanupAfterDrop();
    if (this.zone) {
      this.zone.destroy();
      this.zone = null;
    }
    this.trayPanel = null;
    this.trayImages = [];
    this.getTrayImages = null;
    this.callbacks = null;
  }

  private cleanupAfterDrop(): void {
    if (this.onMove) this.scene.input.off('pointermove', this.onMove);
    if (this.onUp) {
      this.scene.input.off('pointerup', this.onUp);
      this.scene.input.off('pointerupoutside', this.onUp);
    }
    this.onMove = undefined;
    this.onUp = undefined;

    if (this.clone) {
      this.clone.setVisible(false);
      this.clone.destroy();
      this.clone = null;
    }
    this.originalImg = null;
    this.pieceId = null;
  }
}

